import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePermanentLocations, usePermanentRealtimeDealers } from "components/DealersAndLocationsDropdown/hooks";
import { usePermanentRealTimeUser } from "hooks";
import { isAdmin } from "utils/common";

import type { DealersLocationsDropdownValue, DropdownItemProps } from "components";

type Props = {
  allowSelectDealers?: boolean;
  allowSelectAllDealers?: boolean;
  handleOnChange: ({ dealerIds, locationIds }: DealersLocationsDropdownValue) => void;
};

const MAX_DEALER_NAME_CHARS = 3;

export const useDealersAndLocationsDropdown = ({ allowSelectDealers, allowSelectAllDealers, handleOnChange }: Props) => {
  const { t } = useTranslation();
  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const { user } = usePermanentRealTimeUser();
  const [allDealersSelected, setAllDealersSelected] = useState(false);

  const groupedLocationsOptions = useMemo(() => {
    const activeDealers = dealers.filter(element => element.active);
    if (!activeDealers.length) return [];

    const options: DropdownItemProps<string>[] = [];
    activeDealers
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(dealer => {
        const { name: dealerName, id: dealerId } = dealer;

        const userLocations =
          user?.my_locations && user.my_locations.length > 0 ? dealer.locations?.filter(loc => user.my_locations?.includes(loc.id)) : dealer.locations;
        const activeLocations = userLocations?.filter(location => location.active) || [];

        if (activeLocations.length === 0) return;

        options.push({
          icon: "house",
          disabled: allowSelectDealers !== true,
          key: `dealer-${dealerId}`,
          value: `dealer-${dealerId}`,
          labelText: dealerName,
          text: dealerName
        });

        activeLocations
          .sort((a, b) => a.name.localeCompare(b.name))
          .forEach(loc => {
            options.push({
              icon: "location dot",
              text: loc.name,
              labelText: `${dealerName.substring(0, MAX_DEALER_NAME_CHARS)} - ${loc.name}`,
              key: `location-${loc.id}`,
              value: `location-${loc.id}`,
              disabled: false
            });
          });
      });

    if (options.length > 0 && allowSelectAllDealers && user && isAdmin(user.role_id)) {
      options.unshift({
        disabled: false,
        icon: "house",
        text: t("v8_all_dealers", "All dealers"),
        labelText: t("v8_all_dealers", "All dealers"),
        key: "all",
        value: "all"
      });
    }

    return options;
  }, [user?.role_id, dealers, locations, allowSelectDealers, allowSelectAllDealers]);

  const handleOnSelectionChange = (options: string[]) => {
    let dealerIds: number[] = [];
    let locationIds: [dealer_id: number, location_id: number][] = [];

    const isAllDealersSelected = options.includes("all") && !allDealersSelected;

    if (!isAllDealersSelected) {
      options.forEach(option => {
        if (option.startsWith("all")) return;

        if (option.startsWith("dealer-")) {
          try {
            const dealer_id = Number(option.substring(7));
            dealerIds.push(dealer_id);
            locationIds = locationIds.filter(loc => loc[0] !== dealer_id);
          } catch (err) {
            console.error("failed to parse selected dealer", err);
          }
        }

        if (option.startsWith("location-")) {
          try {
            const location_id = Number(option.substring(9));
            const dealer_id = locations.find(loc => loc.id === location_id)?.dealer_id;
            if (dealer_id) {
              locationIds.push([dealer_id, location_id]);
              dealerIds = dealerIds.filter(id => id !== dealer_id);
            }
          } catch (err) {
            console.error("failed to parse selected location", err);
          }
        }
      });
    }

    setAllDealersSelected(isAllDealersSelected);
    handleOnChange({ dealerIds: isAllDealersSelected ? dealers.map(d => d.id) : dealerIds, locationIds: locationIds.map(element => element[1]) });
  };

  return { groupedLocationsOptions, handleOnSelectionChange, allDealersSelected };
};
